import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../utils/axiosConfig'
import { getStorageUrl } from '../../services/apiService'

const AllEvents = () => {
  const navigate = useNavigate()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('upcoming')
  const [showModal, setShowModal] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [players, setPlayers] = useState([])
  const [selectedPlayersByCategory, setSelectedPlayersByCategory] = useState({}) // { "Little League": [1, 2], "Sub Junior": [3] }
  const [registrationNotes, setRegistrationNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [toast, setToast] = useState({ show: false, message: '', type: '' })

  // Category mapping: Short code to Full name
  const categoryMapping = {
    'LL': 'Little League',
    'SJ': 'Sub Junior',
    'JL': 'Junior',
    'SL': 'Senior',
    'U-12': 'Little League',
    'U-15': 'Sub Junior',
    'U-18': 'Junior'
  }

  // Normalize category - convert short code to full name
  const normalizeCategory = (category) => {
    if (!category) return category
    const trimmed = category.trim()
    return categoryMapping[trimmed] || trimmed
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type })
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' })
    }, 4000)
  }

  useEffect(() => {
    fetchEvents()
    fetchPlayers()
  }, [])

  const fetchEvents = async () => {
    try {
      setLoading(true)
      const response = await api.get('/user/events')
      if (response.data.success) {
        // Every tournament, entered or not. Dropping the entered ones emptied
        // the page for exactly the coordinators who had done all their work,
        // and the table below already renders them properly - a count badge
        // and "Add More" instead of "Participate".
        setEvents(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching events:', error)
      if (error.response?.status !== 401) {
        alert('Failed to fetch events')
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchPlayers = async () => {
    try {
      const response = await api.get('/user/my-players', {
        params: { status: 'active' }
      })
      if (response.data.success) {
        setPlayers(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching players:', error)
    }
  }

  const handleParticipateClick = (event) => {
    if (!event.can_register) {
      alert('Registration is not available for this event')
      return
    }
    // Allow adding more players even if some are already registered
    setSelectedEvent(event)
    setSelectedPlayersByCategory({})
    setRegistrationNotes('')
    setShowModal(true)
  }

  const handlePlayerSelect = (category, playerId) => {
    setSelectedPlayersByCategory(prev => {
      const categoryPlayers = prev[category] || []

      if (categoryPlayers.includes(playerId)) {
        // Remove player from this category
        const updated = categoryPlayers.filter(id => id !== playerId)
        if (updated.length === 0) {
          // Remove category if no players selected
          const { [category]: _, ...rest } = prev
          return rest
        }
        return { ...prev, [category]: updated }
      } else {
        // Add player to this category
        return { ...prev, [category]: [...categoryPlayers, playerId] }
      }
    })
  }

  // Get event categories as array
  const getEventCategories = (event) => {
    if (!event.event_level) return []
    return event.event_level.split(',').map(cat => normalizeCategory(cat))
  }

  // Check if player is already registered in a specific category
  const isPlayerRegisteredInCategory = (event, playerId, category) => {
    if (!event.registered_player_categories) return false
    return event.registered_player_categories.some(
      reg => reg.player_id === playerId && reg.category === category
    )
  }

  const handleSubmitParticipation = async (e) => {
    e.preventDefault()

    // Build registrations array from selectedPlayersByCategory
    const registrations = []
    Object.entries(selectedPlayersByCategory).forEach(([category, playerIds]) => {
      playerIds.forEach(playerId => {
        registrations.push({
          player_id: playerId,
          category: category
        })
      })
    })

    if (registrations.length === 0) {
      alert('Please select at least one player for at least one category')
      return
    }

    setSubmitting(true)

    try {
      const response = await api.post(`/user/events/${selectedEvent.id}/participate`, {
        registrations: registrations,
        registration_notes: registrationNotes
      })

      if (response.data.success) {
        showToast(response.data.message, 'success')
        setShowModal(false)
        fetchEvents() // Refresh events list
      }
    } catch (error) {
      console.error('Error registering for event:', error)
      showToast(error.response?.data?.message || 'Failed to register for event', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'published': 'status-published',
      'registration_open': 'status-open',
      'registration_closed': 'status-closed',
      'ongoing': 'status-ongoing',
      'completed': 'status-completed',
      'cancelled': 'status-cancelled'
    }
    return statusMap[status] || 'status-default'
  }

  const getRegistrationStatusBadge = (regStatus) => {
    const statusMap = {
      'open': { text: 'Registration Open', class: 'reg-open' },
      'closed': { text: 'Registration Closed', class: 'reg-closed' },
      'not_started': { text: 'Coming Soon', class: 'reg-coming' },
      'not_available': { text: 'N/A', class: 'reg-na' }
    }
    return statusMap[regStatus] || { text: regStatus, class: 'reg-default' }
  }

  const handleBackClick = () => {
    navigate('/user/dashboard')
  }

  // Filter events by past/upcoming
  // Event is considered past only after the end date day is fully over
  const getFilteredEvents = () => {
    const now = new Date()
    if (activeTab === 'past') {
      return events.filter(event => {
        const endDate = new Date(event.event_end_date)
        endDate.setHours(23, 59, 59, 999)
        return endDate < now || event.status === 'completed'
      })
    } else {
      return events.filter(event => {
        const endDate = new Date(event.event_end_date)
        endDate.setHours(23, 59, 59, 999)
        return endDate >= now && event.status !== 'completed'
      })
    }
  }

  const filteredEvents = getFilteredEvents()

  return (
    <div className="my-events-page">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <i className={`fas fa-${toast.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
          <span>{toast.message}</span>
        </div>
      )}

      <div className="page-header">
        <div className="header-left">
          <div>
            <h1>All Tournaments</h1>
            <p>View and participate in tournaments</p>
          </div>
        </div>
        <button
          className="back-button"
          onClick={handleBackClick}
          type="button"
        >
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>

      {/* Past/Upcoming Tabs */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '1rem',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        display: 'flex',
        gap: '1rem'
      }}>
        <button
          onClick={() => setActiveTab('upcoming')}
          style={{
            flex: 1,
            padding: '1rem',
            border: activeTab === 'upcoming' ? '2px solid #c00f15' : '2px solid #e5e7eb',
            borderRadius: '10px',
            background: activeTab === 'upcoming' ? 'rgba(192, 15, 21, 0.1)' : 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            transition: 'all 0.2s',
            fontSize: '1rem',
            fontWeight: '600',
            color: activeTab === 'upcoming' ? '#c00f15' : '#6b7280'
          }}
        >
          <i className="fas fa-calendar-plus" style={{ fontSize: '1.5rem' }}></i>
          Upcoming Tournaments
        </button>
        <button
          onClick={() => setActiveTab('past')}
          style={{
            flex: 1,
            padding: '1rem',
            border: activeTab === 'past' ? '2px solid #c00f15' : '2px solid #e5e7eb',
            borderRadius: '10px',
            background: activeTab === 'past' ? 'rgba(192, 15, 21, 0.1)' : 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            transition: 'all 0.2s',
            fontSize: '1rem',
            fontWeight: '600',
            color: activeTab === 'past' ? '#c00f15' : '#6b7280'
          }}
        >
          <i className="fas fa-history" style={{ fontSize: '1.5rem' }}></i>
          Past Tournaments
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading events...</p>
        </div>
      ) : filteredEvents.length === 0 ? (
        <div className="no-data">
          <i className="fas fa-calendar-times"></i>
          <p>No {activeTab} tournaments available</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="events-table">
            <thead>
              <tr>
                <th>Tournament Name</th>
                <th>Dates</th>
                <th>Venue</th>
                <th>Level & Type</th>
                <th>Status</th>
                <th>Registration</th>
                <th>Registered</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEvents.map(event => {
                const regStatus = getRegistrationStatusBadge(event.registration_status)
                return (
                  <tr key={event.id} className="event-row">
                    <td className="event-name-cell">
                      <div className="event-name-wrapper">
                        {event.event_image && (
                          <img
                            src={getStorageUrl(event.event_image)}
                            alt={event.event_name}
                            className="event-thumbnail"
                          />
                        )}
                        <div>
                          <div className="event-name">{event.event_name}</div>
                          {event.event_description && (
                            <div className="event-desc">{event.event_description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>{event.formatted_dates}</td>
                    <td>{event.event_venue}{event.event_city ? `, ${event.event_city}` : ''}</td>
                    <td>{event.level_name} - {event.type_name}</td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(event.status)}`}>
                        {event.status_label}
                      </span>
                    </td>
                    <td>
                      <span className={`reg-status ${regStatus.class}`}>
                        {regStatus.text}
                      </span>
                    </td>
                    <td>
                      {event.has_participated ? (
                        <span className="participated-badge">
                          <i className="fas fa-check-circle"></i> {event.registered_players_count}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="actions-cell">
                      {event.can_register ? (
                        <button
                          className="btn-participate"
                          onClick={() => navigate(`/user/events/${event.id}/participate`)}
                          title={event.has_participated ? 'Add More Players' : 'Participate'}
                        >
                          <i className="fas fa-user-plus"></i>
                          {event.has_participated ? 'Add More' : 'Participate'}
                        </button>
                      ) : (
                        <span className="cannot-register">N/A</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Participate Modal */}
      {showModal && selectedEvent && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Participate in Event</h2>
              <button className="close-button" onClick={() => setShowModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="event-info">
                <h3>{selectedEvent.event_name}</h3>
                <p>{selectedEvent.formatted_dates}</p>
                <p>{selectedEvent.event_venue}</p>
              </div>

              <form onSubmit={handleSubmitParticipation}>
                <div className="event-category-info">
                  <p><strong>Event Categories:</strong> {selectedEvent.level_name}</p>
                  <p className="info-note">Select players for each category. A player can participate in multiple categories.</p>
                </div>

                {players.length === 0 ? (
                  <p className="no-players">You have no active players. Please add players first.</p>
                ) : (
                  <div className="categories-container">
                    {getEventCategories(selectedEvent).map(category => (
                      <div key={category} className="category-section">
                        <h4 className="category-title">
                          <i className="fas fa-tag"></i> {category}
                        </h4>
                        <div className="players-list">
                          {players.map(player => {
                            const isAlreadyRegistered = isPlayerRegisteredInCategory(selectedEvent, player.id, category)
                            const isSelected = selectedPlayersByCategory[category]?.includes(player.id)

                            return (
                              <label
                                key={player.id}
                                className={`player-checkbox ${isAlreadyRegistered ? 'already-registered' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected || false}
                                  onChange={() => handlePlayerSelect(category, player.id)}
                                  disabled={isAlreadyRegistered}
                                />
                                <div className="player-info">
                                  <span className="player-name">
                                    {player.full_name}
                                    {isAlreadyRegistered && (
                                      <span className="registered-indicator">
                                        <i className="fas fa-check-circle"></i> Already Registered
                                      </span>
                                    )}
                                  </span>
                                  <span className="player-details">
                                    {player.age} years • {player.gender} • {player.weight ? `${player.weight} kg` : 'Weight N/A'} • {player.player_reg_id}
                                  </span>
                                </div>
                              </label>
                            )
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="form-group">
                  <label>Registration Notes (Optional)</label>
                  <textarea
                    value={registrationNotes}
                    onChange={(e) => setRegistrationNotes(e.target.value)}
                    rows="3"
                    placeholder="Any additional information..."
                  ></textarea>
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => setShowModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={submitting || Object.keys(selectedPlayersByCategory).length === 0}
                  >
                    {submitting ? 'Registering...' : 'Register Players'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .my-events-page {
          padding: 2rem;
          width: 100%;
          background: #f9fafb;
          min-height: 100vh;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          background: white;
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .header-left {
          display: flex;
          gap: 1.5rem;
          align-items: center;
        }

        .back-button {
          padding: 0.75rem 1.5rem;
          background: #c00f15;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.95rem;
          font-weight: 600;
          transition: all 0.3s;
          position: relative;
          z-index: 10;
          pointer-events: auto;
        }

        .back-button:hover {
          background: #a00d12;
        }

        .back-button:active {
          transform: scale(0.98);
        }

        .page-header h1 {
          color: #c00f15;
          margin-bottom: 0.25rem;
        }

        .page-header p {
          color: #6b7280;
        }

        .loading-state, .no-data {
          text-align: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .loading-state i, .no-data i {
          font-size: 3rem;
          margin-bottom: 1rem;
          color: #9ca3af;
        }

        .loading-state p, .no-data p {
          color: #6b7280;
          font-size: 1.1rem;
        }

        .table-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow-x: auto;
        }

        .events-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }

        .events-table thead {
          background: #f9fafb;
        }

        .events-table th {
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          color: #374151;
          border-bottom: 2px solid #e5e7eb;
          white-space: nowrap;
        }

        .events-table td {
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
          color: #6b7280;
          vertical-align: middle;
        }

        .event-row:hover {
          background: #f9fafb;
        }

        .event-name-cell {
          min-width: 300px;
        }

        .event-name-wrapper {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .event-thumbnail {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          object-fit: cover;
          flex-shrink: 0;
        }

        .event-name {
          color: #c00f15;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }

        .event-desc {
          color: #6b7280;
          font-size: 0.75rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.4;
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
          display: inline-block;
          white-space: nowrap;
        }

        .status-published {
          background: #fce4ec;
          color: #c00f15;
        }

        .status-open {
          background: #dcfce7;
          color: #166534;
        }

        .status-closed {
          background: #fee2e2;
          color: #991b1b;
        }

        .status-ongoing {
          background: #fef3c7;
          color: #92400e;
        }

        .status-completed {
          background: #f3f4f6;
          color: #374151;
        }

        .participated-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
          background: #dcfce7;
          color: #166534;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          white-space: nowrap;
        }

        .reg-status {
          font-weight: 500;
        }

        .reg-open {
          color: #166534;
        }

        .reg-closed {
          color: #991b1b;
        }

        .reg-coming {
          color: #92400e;
        }

        .actions-cell {
          white-space: nowrap;
        }

        .btn-participate {
          padding: 0.5rem 1rem;
          background: linear-gradient(135deg, #c00f15 0%, #a00d12 100%);
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          font-size: 0.875rem;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s;
          white-space: nowrap;
          box-shadow: 0 2px 6px rgba(192, 15, 21, 0.3);
        }

        .btn-participate:hover {
          background: linear-gradient(135deg, #a00d12 0%, #8a0b10 100%);
          box-shadow: 0 4px 12px rgba(192, 15, 21, 0.4);
        }

        .cannot-register {
          color: #9ca3af;
          font-size: 0.875rem;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 2rem;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          color: #111827;
          margin: 0;
        }

        .close-button {
          background: none;
          border: none;
          font-size: 1.5rem;
          color: #6b7280;
          cursor: pointer;
          padding: 0.5rem;
          transition: all 0.3s;
        }

        .close-button:hover {
          color: #111827;
        }

        .modal-body {
          padding: 1.5rem;
        }

        .event-info {
          background: #f9fafb;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }

        .event-info h3 {
          color: #111827;
          margin-bottom: 0.5rem;
        }

        .event-info p {
          color: #6b7280;
          font-size: 0.875rem;
          margin: 0.25rem 0;
        }

        .event-category-info {
          background: #fef2f2;
          border-left: 4px solid #c00f15;
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }

        .event-category-info p {
          margin: 0.25rem 0;
          color: #c00f15;
        }

        .event-category-info .info-note {
          font-size: 0.875rem;
          color: #6b7280;
          font-style: italic;
          margin-top: 0.5rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          color: #374151;
          font-weight: 500;
          margin-bottom: 0.5rem;
        }

        /* Removed weight-filter-select - automatic filtering now */

        .no-players {
          color: #ef4444;
          font-size: 0.875rem;
          padding: 1rem;
          background: #fef2f2;
          border-radius: 8px;
          text-align: center;
        }

        .categories-container {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          max-height: 500px;
          overflow-y: auto;
          padding: 0.5rem;
        }

        .category-section {
          background: #f9fafb;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 1rem;
        }

        .category-title {
          color: #c00f15;
          font-size: 1rem;
          font-weight: 600;
          margin: 0 0 1rem 0;
          padding: 0.5rem;
          background: white;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .category-title i {
          font-size: 0.9rem;
        }

        .players-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          max-height: 250px;
          overflow-y: auto;
          padding: 0.5rem;
        }

        .player-checkbox {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s;
        }

        .player-checkbox:hover {
          border-color: #c00f15;
          background: #f9fafb;
        }

        .player-checkbox.already-registered {
          background: #eff6ff;
          border-color: #bfdbfe;
          cursor: not-allowed;
        }

        .player-checkbox.already-registered:hover {
          border-color: #bfdbfe;
          background: #eff6ff;
        }

        .player-checkbox input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .player-checkbox input[type="checkbox"]:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        .player-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .player-name {
          color: #111827;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .registered-indicator {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          color: #2563eb;
          font-size: 0.75rem;
          font-weight: 500;
          padding: 0.125rem 0.5rem;
          background: #dbeafe;
          border-radius: 12px;
        }

        .registered-indicator i {
          font-size: 0.875rem;
        }

        .player-details {
          color: #6b7280;
          font-size: 0.875rem;
        }

        .form-group textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 1rem;
          resize: vertical;
          font-family: inherit;
        }

        .form-group textarea:focus {
          outline: none;
          border-color: #c00f15;
          box-shadow: 0 0 0 3px rgba(192, 15, 21, 0.1);
        }

        .modal-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          padding-top: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        .btn-cancel, .btn-submit {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s;
        }

        .btn-cancel {
          background: #f3f4f6;
          color: #374151;
        }

        .btn-cancel:hover {
          background: #e5e7eb;
        }

        .btn-submit {
          background: #10b981;
          color: white;
        }

        .btn-submit:hover {
          background: #059669;
        }

        .btn-submit:disabled, .btn-cancel:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* Toast Notification */
        .toast {
          position: fixed;
          top: 2rem;
          right: 2rem;
          padding: 1rem 1.5rem;
          background: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          display: flex;
          align-items: center;
          gap: 0.75rem;
          z-index: 10000;
          animation: slideIn 0.3s ease-out;
          min-width: 300px;
          max-width: 500px;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .toast-success {
          border-left: 4px solid #10b981;
        }

        .toast-success i {
          color: #10b981;
          font-size: 1.25rem;
        }

        .toast-error {
          border-left: 4px solid #ef4444;
        }

        .toast-error i {
          color: #ef4444;
          font-size: 1.25rem;
        }

        .toast span {
          color: #374151;
          font-size: 0.95rem;
          line-height: 1.5;
        }

        @media (max-width: 768px) {
          .my-events-page {
            padding: 1rem;
          }

          .page-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
            padding: 1rem;
          }

          .header-left {
            flex-direction: column;
            align-items: flex-start;
          }

          .table-container {
            overflow-x: auto;
          }

          .events-table {
            font-size: 0.75rem;
          }

          .events-table th,
          .events-table td {
            padding: 0.75rem 0.5rem;
          }

          .event-thumbnail {
            width: 40px;
            height: 40px;
          }

          .modal-overlay {
            padding: 1rem;
          }

          .toast {
            top: 1rem;
            right: 1rem;
            left: 1rem;
            min-width: auto;
          }
        }
      `}</style>
    </div>
  )
}

export default AllEvents
